import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { analyticsService } from '../services/analyticsService';
import { leadsService } from '../services/leadsService';
import { supabase } from '../supabase/supabase';
import { useNavigate } from 'react-router-dom';

export default function Dashboard() {
  const [activeTab, setActiveTab] = useState('leads');
  const [timeRange, setTimeRange] = useState('all');
  const [stats, setStats] = useState({ totalVisits: 0, uniqueVisitors: 0, avgDuration: 0 });
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [timeRange]);

  const fetchData = async () => {
    setLoading(true);
    const [statsData, leadsData] = await Promise.all([
      analyticsService.getStats(timeRange),
      leadsService.getLeads(timeRange)
    ]);
    setStats(statsData);
    setLeads(leadsData);
    setLoading(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white pt-24 pb-12 px-8">
      <div className="max-w-[1600px] mx-auto">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-8">
          <div>
            <div className="inline-flex items-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-stark-pink animate-pulse" />
              <span className="text-stark-pink font-black text-xs tracking-widest uppercase">Admin Terminal</span>
            </div>
            <h1 className="text-6xl md:text-8xl font-black uppercase tracking-tighter leading-none">Intelligence</h1>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="bg-white/5 border border-white/10 p-1 rounded-full flex">
              {['all', 'today', 'week', 'month'].map((range) => (
                <button
                  key={range}
                  onClick={() => setTimeRange(range)}
                  className={`px-6 py-2 rounded-full text-xs font-black uppercase transition-all ${
                    timeRange === range ? 'bg-stark-lime text-black' : 'hover:text-white/60'
                  }`}
                >
                  {range}
                </button>
              ))}
            </div>
            <button 
              onClick={handleLogout}
              className="w-12 h-12 glass-card rounded-full flex items-center justify-center hover:bg-red-500/20 transition-colors"
            >
              <SafeIcon icon={FiIcons.FiLogOut} />
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-3 gap-1 mb-1">
          <StatCard title="Total Visits" value={stats.totalVisits} icon={FiIcons.FiEye} />
          <StatCard title="Unique Visitors" value={stats.uniqueVisitors} icon={FiIcons.FiUsers} />
          <StatCard title="Avg. Time" value={`${Math.floor(stats.avgDuration / 60)}m ${stats.avgDuration % 60}s`} icon={FiIcons.FiClock} />
        </div>

        {/* Content Area */}
        <div className="mt-12">
          <div className="flex gap-8 border-b border-white/5 mb-8">
            <button 
              onClick={() => setActiveTab('leads')}
              className={`pb-4 text-sm font-black uppercase tracking-widest border-b-2 transition-all ${
                activeTab === 'leads' ? 'border-stark-lime text-stark-lime' : 'border-transparent text-white/40 hover:text-white'
              }`}
            >
              Captured Leads ({leads.length})
            </button>
          </div>

          {loading ? (
            <div className="h-64 flex items-center justify-center text-white/20 font-black uppercase tracking-widest">Loading Intelligence...</div>
          ) : (
            <div className="space-y-4">
              {leads.map((lead) => (
                <LeadRow key={lead.id} lead={lead} />
              ))}
              
              {leads.length === 0 && (
                <div className="p-20 text-center glass-card rounded-3xl">
                  <p className="text-white/20 font-black uppercase tracking-widest">No leads captured in this timeframe.</p>
                </div>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

function StatCard({ title, value, icon: Icon }) {
  return (
    <div className="glass-card p-12 hover:bg-white/10 transition-colors">
      <div className="flex justify-between items-start mb-8">
        <span className="text-xs font-black uppercase tracking-widest text-white/40">{title}</span>
        <SafeIcon icon={Icon} className="text-stark-pink text-xl" />
      </div>
      <div className="text-6xl font-black tracking-tighter">{value}</div>
    </div>
  );
}

function LeadRow({ lead }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="glass-card rounded-3xl overflow-hidden group">
      <div 
        onClick={() => setExpanded(!expanded)}
        className="p-8 flex flex-col md:flex-row md:items-center justify-between gap-6 cursor-pointer hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-6">
          <div className="w-12 h-12 bg-stark-lime/10 rounded-full flex items-center justify-center text-stark-lime">
            <SafeIcon icon={FiIcons.FiUser} />
          </div>
          <div>
            <h3 className="text-xl font-black uppercase tracking-tight">{lead.name || 'Anonymous'}</h3>
            <p className="text-sm text-white/40 font-medium">{lead.email || 'No email provided'}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-12">
          <div className="text-right hidden md:block">
            <p className="text-xs font-black uppercase tracking-widest text-white/20 mb-1">Company</p>
            <p className="font-bold">{lead.company || 'N/A'}</p>
          </div>
          <div className="text-right">
            <p className="text-xs font-black uppercase tracking-widest text-white/20 mb-1">Received</p>
            <p className="font-bold">{format(new Date(lead.created_at), 'MMM dd, HH:mm')}</p>
          </div>
          <div className={`w-10 h-10 rounded-full flex items-center justify-center border border-white/10 transition-transform ${expanded ? 'rotate-180 bg-stark-lime text-black border-stark-lime' : ''}`}>
            <SafeIcon icon={FiIcons.FiChevronDown} />
          </div>
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-white/5 bg-black/40"
          >
            <div className="p-12">
              <h4 className="text-stark-pink font-black uppercase tracking-widest text-sm mb-8">Project Brief</h4>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/40 mb-3 flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiBriefcase} /> Project Type
                  </p>
                  <p className="text-lg font-medium text-white/80">{lead.project_type || 'Not answered'}</p>
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/40 mb-3 flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiTarget} /> Problem to Solve
                  </p>
                  <p className="text-lg font-medium text-white/80">{lead.problem || 'Not answered'}</p>
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/40 mb-3 flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiUsers} /> Target Users
                  </p>
                  <p className="text-lg font-medium text-white/80">{lead.users_count || 'Not answered'}</p>
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/40 mb-3 flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiClock} /> Timeline
                  </p>
                  <p className="text-lg font-medium text-white/80">{lead.timeline || 'Not answered'}</p>
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/40 mb-3 flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiLink} /> Integrations Needed
                  </p>
                  <p className="text-lg font-medium text-white/80">{lead.integrations || 'Not answered'}</p>
                </div>
              </div>

              <div className="w-full h-[1px] bg-white/5 mb-8" />
              
              <h4 className="text-stark-pink font-black uppercase tracking-widest text-sm mb-8">Contact Information</h4>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/40 mb-2">Name</p>
                  <p className="text-md font-medium text-white/80">{lead.name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/40 mb-2">Email</p>
                  <a href={`mailto:${lead.email}`} className="text-md font-medium text-stark-lime hover:underline">{lead.email || 'N/A'}</a>
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/40 mb-2">Company</p>
                  <p className="text-md font-medium text-white/80">{lead.company || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/40 mb-2">Website</p>
                  {lead.website ? (
                    <a href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`} target="_blank" rel="noopener noreferrer" className="text-md font-medium text-stark-lime hover:underline">
                      {lead.website}
                    </a>
                  ) : (
                    <p className="text-md font-medium text-white/80">Not provided</p>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}